import { featuresForCadence } from '../Settings/Subscription/billingStatus.js';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Box, Button, Card, CardContent, 
  CardActions, CircularProgress, Divider, TextField } from '@mui/material';
  import Grid from '@mui/material/Grid2';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StripeElements from '../../components/payments/StripeElements';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import useAlert from '../../alert/useAlert.js';
import { SUBSCRIPTION_PLANS, CREDIT_HKD, CREDIT_COSTS } from '../../constants/subscriptionPlans.js';
import { authedFetch } from '../../utils/backendFetch.js';
import { activateFreePlan } from '../../utils/freePlanActivation.js';



// Free-plan activation is server-authoritative: activate_free_plan() decides
// eligibility, writes subscription_info and grants the one-time credit trial in
// one transaction. The browser only reports the outcome.
const FreeCheckoutForm = ({ plan, onSubscriptionComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { setAlert } = useAlert();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const outcome = await activateFreePlan(plan);
      setAlert(outcome.message, outcome.severity);
      // already_granted / paid_plan_active mean the account is provisioned;
      // nothing was deposited, but repeating the call cannot change that, so
      // complete the flow instead of stranding the user on this form.
      if (outcome.settled) {
        onSubscriptionComplete();
      }
    } catch (error) {
      setAlert(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Button 
        type="submit" 
        variant="contained" 
        fullWidth 
        disabled={isLoading} 
        sx={{ 
          mt: 2, 
          py: 1.5, 
          backgroundColor: '#29C0BB', 
          '&:hover': { backgroundColor: '#1D8A87' } 
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : `Activate ${plan.name} Plan`}
      </Button>
    </form>
  );
};

const CheckoutForm = ({ plan, onSubscriptionComplete, name, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  // Price in the smallest currency unit, from the shared plan constants.
  // Annual (D-022) is billed monthly at the 10%-off rate.
  const originalPrice = plan.cadence === 'annual' && plan.annual
    ? plan.annual.priceCents : plan.priceCents;

  const [isLoading, setIsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');
  const [couponValid, setCouponValid] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const [couponDiscount, setCouponDiscount] = useState(null);

  const { setAlert } = useAlert();

  // Every edit and every new request bumps this generation counter; a response
  // is applied only if it is still the newest. Without it a reply for `SAVE50`
  // can land after the field has already become `SAVE50X`, re-enabling the old
  // discount against the new code — the exact display/submit divergence the
  // reset below exists to prevent, just inside a ~300ms window.
  const couponRequestGeneration = useRef(0);

  // Editing the code invalidates the previous validation. The submitted body
  // now carries the raw `couponCode` rather than the `couponId` returned by
  // validate-coupon, so a stale `couponValid` would let the displayed total
  // (from the old coupon) disagree with the code actually sent.
  const handleCouponCodeChange = (event) => {
    setCouponCode(event.target.value);
    setCouponValid(false);
    setCouponDiscount(null);
    setCouponMessage('');
    // Supersede any validation still in flight.
    couponRequestGeneration.current += 1;
    setIsValidatingCoupon(false);
  };

  const calculateFinalPrice = () => {
    if (!couponValid || !couponDiscount) return originalPrice;
    
    if (couponDiscount.amountOff) {
      return Math.max(0, originalPrice - couponDiscount.amountOff);
    } else if (couponDiscount.percentOff) {
      return Math.max(0, originalPrice - (originalPrice * couponDiscount.percentOff / 100));
    }
    return originalPrice;
  };

  const validateCoupon = async () => {
    const requestedCode = couponCode.trim();
    if (!requestedCode) return;

    const generation = (couponRequestGeneration.current += 1);
    const isStale = () => couponRequestGeneration.current !== generation;

    setIsValidatingCoupon(true);
    setCouponMessage('');
    setCouponValid(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          couponCode: requestedCode,
        }),
      });

      const data = await response.json();

      // The field moved on while we were waiting: this answer is about a code
      // the user is no longer submitting, so it must not touch the total.
      if (isStale()) return;

      if (!response.ok) {
        throw new Error(data.error || 'Failed to validate coupon');
      }
      
      if (data.valid) {
        setCouponValid(true);
        setCouponDiscount({
          amountOff: data.amountOff,
          percentOff: data.percentOff
        });
        setCouponMessage(`${data.description}: ${data.amountOff ? `$${data.amountOff/100} off` : `${data.percentOff}% off`}`);
      } else {
        setCouponMessage(data.message || 'Invalid coupon');
      }
    } catch (error) {
      if (!isStale()) setCouponMessage(error.message);
    } finally {
      // A newer request (or an edit) owns the spinner from here on.
      if (!isStale()) setIsValidatingCoupon(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }
      
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        elements,
        params: {
          billing_details: {
            name: name,
            email: email,
          },
        },
      });
      
      if (paymentMethodError) {
        throw new Error(paymentMethodError.message);
      }

      // The backend derives the user, the Stripe customer and the Stripe price
      // from the verified session + tier. Never send userId/customerId/priceId.
      const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/create-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tier: plan.tier,
          cadence: plan.cadence || 'monthly',
          couponCode: couponValid ? couponCode.trim() : null,
          paymentMethodId: paymentMethod.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = errorData.error || 'Failed to process subscription';
        if (errorMessage.includes(':')) {
          errorMessage = errorMessage.split(':').pop().trim();
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
    
      if (data.requiresAction) {
        const { error } = await stripe.confirmCardPayment(data.clientSecret);
        if (error) {
          throw new Error(error.message);
        }
      }
      setAlert('Subscription activated successfully!', 'success');
      onSubscriptionComplete();
    } catch (error) {
      setAlert(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <PaymentElement 
          options={{
            wallets: {
              applePay: 'never',
              googlePay: 'never',
              link: 'never'
            }
          }}
        />
      </Box>

      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Have a coupon?
        </Typography>
        <TextField
          label="Coupon Code"
          variant="outlined"
          fullWidth
          value={couponCode}
          onChange={handleCouponCodeChange}
          sx={{ mb: 1 }}
        />
        {couponMessage && (
          <Typography 
            variant="body2" 
            color={couponValid ? "success.main" : "error.main"}
            sx={{ mt: 1 }}
          >
            {couponMessage}
          </Typography>
        )}
        <Button
          variant="text"
          onClick={validateCoupon}
          disabled={!couponCode || isValidatingCoupon}
          sx={{ color: '#29C0BB' }}
        >
          {isValidatingCoupon ? <CircularProgress size={20} /> : 'Apply Coupon'}
        </Button>
      </Box>

      {/* Price Summary Section */}
      <Box sx={{ mt: 3, mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Order Summary
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body1">{plan.name} Plan</Typography>
          <Typography variant="body1">${(originalPrice / 100).toFixed(2)} HKD</Typography>
        </Box>
        
        {couponValid && couponDiscount && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'success.main' }}>
            <Typography variant="body2">Coupon Discount</Typography>
            <Typography variant="body2">
              -${couponDiscount.amountOff ? 
                (couponDiscount.amountOff / 100).toFixed(2) : 
                ((originalPrice * couponDiscount.percentOff / 100) / 100).toFixed(2)} HKD
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight="bold">Total</Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            ${(calculateFinalPrice() / 100).toFixed(2)} HKD
          </Typography>
        </Box>
      </Box>
      
      <Button 
        type="submit" 
        variant="contained" 
        fullWidth 
        disabled={!stripe || isLoading} 
        sx={{ 
          mt: 2, 
          py: 1.5, 
          backgroundColor: '#29C0BB', 
          '&:hover': { backgroundColor: '#1D8A87' } 
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : `Activate ${plan.name} Plan`}
      </Button>
    </form>
  );
};

const PlanOption = ({ plan, selected, onSelect, cadence = 'monthly' }) => {
  return (
    <Card 
      raised={selected} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderColor: selected ? '#29C0BB' : 'transparent',
        borderWidth: 2,
        borderStyle: 'solid',
        boxShadow: selected ? '0 8px 16px rgba(41, 192, 187, 0.2)' : undefined,
        transition: 'all 0.3s ease'
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" component="div" fontWeight="bold" gutterBottom>
          {plan.name}
        </Typography>
        <Typography variant="h4" color="primary" gutterBottom>
          {cadence === 'annual' && plan.annual
            ? `${plan.annual.displayPrice}/month`
            : plan.price}
        </Typography>
        {cadence === 'annual' && plan.annual && (
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {plan.annual.note}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {plan.description}
        </Typography>
        <Divider sx={{ my: 2 }} />
        {featuresForCadence(plan, cadence).map((feature, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CheckCircleOutlineIcon sx={{ mr: 1, color: '#29C0BB' }} fontSize="small" />
            <Typography variant="body2">{feature}</Typography>
          </Box>
        ))}
      </CardContent>
      <CardActions>
        <Button 
          fullWidth 
          variant={selected ? "contained" : "outlined"} 
          onClick={() => onSelect(plan.name)}
          sx={{ 
            backgroundColor: selected ? '#29C0BB' : 'transparent', 
            color: selected ? 'white' : '#29C0BB',
            borderColor: '#29C0BB',
            '&:hover': { 
              backgroundColor: selected ? '#1D8A87' : 'rgba(41, 192, 187, 0.1)',
              borderColor: '#29C0BB'
            }
          }}
        >
          {selected ? 'Selected' : 'Select Plan'}
        </Button>
      </CardActions>
    </Card>
  );
};

const CreateSubscription = ({ name, email, onSubscriptionComplete, initialPlan = null, initialCadence = 'monthly', checkoutOnly = false }) => {
  const [selectedPlan, setSelectedPlan] = useState(['Basic', 'Pro'].includes(initialPlan) ? initialPlan : null);
  // D-022 billing cadence: annual is billed monthly at 10% off with a
  // 12-month minimum term. Free ignores the toggle.
  const [cadence, setCadence] = useState(initialCadence === 'annual' ? 'annual' : 'monthly');
  const [clientSecret, setClientSecret] = useState('');
  const { setAlert } = useAlert();

  useEffect(() => {
    if (selectedPlan && selectedPlan !== 'Free') {
      const fetchPaymentIntent = async () => {
        try {
          const response = await authedFetch(`${process.env.REACT_APP_STRIPE_SUBSCRIPTION_URL}/api/create-setup-intent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, email }),
          });
          
          const { clientSecret } = await response.json();
          setClientSecret(clientSecret);
        } catch (error) {
          setAlert('Failed to initialize payment', 'error');
          console.error('Error fetching payment intent:', error);
        }
      };
      
      fetchPaymentIntent();
    }
  }, [selectedPlan, name, email, setAlert]);

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#29C0BB',
      }
    },
    paymentMethodCreation: 'manual',
    linkAuthenticationElement: {
      mode: 'disabled'
    },
    wallet: {
      applePay: 'never',
      googlePay: 'never',
      link: 'never'
    }
  };

  return (
    <Container>
      {!checkoutOnly && <>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Choose Your Subscription Plan
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select the plan that best fits your needs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 640, mx: 'auto' }}>
          All plans use credits (1 credit = HK${CREDIT_HKD}). Live transcription costs{' '}
          {CREDIT_COSTS.liveTranscriptionPerHour} credits/hour, recorded audio{' '}
          {CREDIT_COSTS.recordedTranscriptionPerHour} credit/hour, and each report{' '}
          {CREDIT_COSTS.report} credit. Connecting your own client costs{' '}
          {CREDIT_COSTS.clientConnectionPerMonth} credits/month — clients who book you
          through MindVault connect free.
        </Typography>
      </Box>

      {/* D-022 cadence toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
        {['monthly', 'annual'].map((option) => (
          <Box
            key={option}
            onClick={() => setCadence(option)}
            sx={{
              px: 2, py: 0.75, borderRadius: 5, cursor: 'pointer',
              border: '1px solid',
              borderColor: cadence === option ? '#29C0BB' : 'rgba(0,0,0,0.2)',
              backgroundColor: cadence === option ? 'rgba(41, 192, 187, 0.12)' : 'transparent',
            }}
          >
            <Typography variant="body2" fontWeight={cadence === option ? 700 : 400}
              sx={{ color: cadence === option ? '#1D8A87' : 'text.secondary' }}>
              {option === 'monthly' ? 'Monthly' : 'Annual — 10% off'}
            </Typography>
          </Box>
        ))}
        {cadence === 'annual' && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Billed monthly · 12-month minimum term
          </Typography>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <PlanOption
            plan={SUBSCRIPTION_PLANS.FREE}
            selected={selectedPlan === 'Free'}
            onSelect={setSelectedPlan}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <PlanOption
            plan={SUBSCRIPTION_PLANS.BASIC}
            selected={selectedPlan === 'Basic'}
            onSelect={setSelectedPlan}
            cadence={cadence}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4}}>
          <PlanOption
            plan={SUBSCRIPTION_PLANS.PRO}
            selected={selectedPlan === 'Pro'}
            onSelect={setSelectedPlan}
            cadence={cadence}
          />
        </Grid>
      </Grid>
      
      </>}
      {selectedPlan && (
        <Box sx={{ maxWidth: 500, mx: 'auto', p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Complete Your {selectedPlan} Plan Subscription
          </Typography>
          {selectedPlan === 'Free' ? (
            <FreeCheckoutForm
              plan={SUBSCRIPTION_PLANS.FREE}
              onSubscriptionComplete={onSubscriptionComplete}
            />
          ) : clientSecret ? (
            <StripeElements options={options}>
              <CheckoutForm
                plan={{ ...(selectedPlan === 'Basic' ? SUBSCRIPTION_PLANS.BASIC : SUBSCRIPTION_PLANS.PRO), cadence }}
                onSubscriptionComplete={onSubscriptionComplete}
                name={name}
                email={email}
              />
            </StripeElements>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default CreateSubscription;
